import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Bell, CheckCircle2, Info, AlertCircle, X } from 'lucide-react';
import { cn } from '../lib/utils';

// Match PayZen reference: app/dashboard/notifications/page.js
const getIcon = (type) => {
  switch (type) {
    case 'success':
      return <CheckCircle2 className="h-6 w-6 text-emerald-600" />;
    case 'alert':
      return <AlertCircle className="h-6 w-6 text-amber-600" />;
    case 'info':
      return <Info className="h-6 w-6 text-blue-600" />;
    default:
      return <Bell className="h-6 w-6 text-gray-600" />;
  }
};

const getIconBg = (type) => {
  switch (type) {
    case 'success':
      return 'bg-emerald-100';
    case 'alert':
      return 'bg-amber-100';
    case 'info':
      return 'bg-blue-100';
    default:
      return 'bg-gray-100';
  }
};

const mapApiTypeToVariant = (type) => {
  switch (type) {
    case 'kyc':
    case 'payment':
    case 'transfer_done':
      return 'success';
    case 'transfer':
    case 'info':
      return 'info';
    case 'fx':
    case 'security':
      return 'alert';
    default:
      return 'info';
  }
};

function NotificationCard({ notification, onMarkAsRead, onDismiss, onShowDetail, isHighlight }) {
  const variant = mapApiTypeToVariant(notification.type);
  const isUnread = notification.isNew ?? !notification.read;

  return (
    <div>
      <Card
        className={cn(
          'bg-white border-[#DCE4E0] hover:shadow-md transition-shadow cursor-pointer',
          isUnread && 'border-l-4 border-l-[#0B6445]',
          isHighlight && 'ring-2 ring-[#0B6445] ring-offset-2'
        )}
        onClick={() => onShowDetail?.(notification)}
      >
        <CardContent className="p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className={cn('flex h-12 w-12 items-center justify-center rounded-full shrink-0', getIconBg(variant))}>
              {getIcon(variant)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-[#073B2E]">{notification.title}</h3>
                {isUnread && (
                  <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium text-white bg-[#0B6445]">
                    New
                  </span>
                )}
              </div>
              <p className="text-sm text-[#0E1B16] opacity-70 mb-2">
                {notification.message || `1 ${notification.fx_from || 'AUD'} = — ${notification.fx_to || 'USD'}`}
              </p>
              <p className="text-xs text-[#0E1B16] opacity-50">{notification.time}</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            {isUnread && (
              <Button variant="ghost" size="icon" onClick={() => onMarkAsRead(notification.id)} className="h-8 w-8" aria-label="Mark as read">
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => onDismiss(notification.id)} className="h-8 w-8" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

const Notifications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailNotification, setDetailNotification] = useState(null);
  const highlightId = searchParams.get('highlight');
  const cardRefs = useRef({});

  const { api } = useAuth();
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllRead,
    removeNotification,
    fetchNotifications,
  } = useNotifications();
  const [simulating, setSimulating] = useState(false);

  // Only auto-open detail modal for unread notifications; clear highlight from URL so refetches don't re-open popup
  useEffect(() => {
    if (!highlightId || !notifications.length) return;
    const n = notifications.find((n) => n.id === highlightId);
    if (n) {
      const isUnread = n.isNew ?? !n.read;
      if (isUnread) setDetailNotification(n);
      cardRefs.current[highlightId]?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('highlight');
        return p.toString() ? p : {};
      }, { replace: true });
    }
  }, [highlightId, notifications, setSearchParams]);

  const closeDetailAndMarkRead = () => {
    if (detailNotification) {
      const isUnread = detailNotification.isNew ?? !detailNotification.read;
      if (isUnread) markAsRead(detailNotification.id);
    }
    setDetailNotification(null);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('highlight');
      return p.toString() ? p : {};
    }, { replace: true });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="notifications-page" className="min-h-screen bg-[#F7FAF9]">
        <div className="max-w-3xl mx-auto py-8 px-4">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#073B2E]">Notifications</h1>
              <p className="text-[#0E1B16] opacity-70 mt-1">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                  : 'All caught up!'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={async () => {
                  setSimulating(true);
                  try {
                    try {
                      await api.post('notifications/simulate-transfer-success');
                    } catch (err) {
                      if (err.response?.status === 404) {
                        const base = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';
                        const token = localStorage.getItem('token');
                        const r = await fetch(`${base}/notifications/simulate-transfer-success`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: '{}',
                        });
                        if (!r.ok) throw new Error(`Simulate failed: ${r.status}`);
                      } else throw err;
                    }
                    await fetchNotifications();
                  } finally {
                    setSimulating(false);
                  }
                }}
                disabled={simulating}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-50 hover:text-amber-900"
              >
                {simulating ? 'Simulating…' : 'Simulate transfer success (test)'}
              </Button>
              {unreadCount > 0 && (
                <Button variant="outline" onClick={markAllRead} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
                  Mark all as read
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {notifications.map((notification) => (
              <div key={notification.id} className="min-h-0" ref={(el) => { cardRefs.current[notification.id] = el; }}>
                <NotificationCard
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDismiss={removeNotification}
                  onShowDetail={setDetailNotification}
                  isHighlight={highlightId === notification.id}
                />
              </div>
            ))}
          </div>

          <Dialog open={!!detailNotification} onOpenChange={(open) => !open && closeDetailAndMarkRead()}>
            <DialogContent className="max-w-md" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="text-[#073B2E]">{detailNotification?.title}</DialogTitle>
              </DialogHeader>
              {detailNotification && (
                <div className="space-y-4">
                  <p className="text-sm text-[#0E1B16] opacity-80">{detailNotification.message}</p>
                  <p className="text-xs text-[#0E1B16] opacity-50">{detailNotification.time}</p>
                  {detailNotification.details && Object.keys(detailNotification.details).length > 0 && (
                    <div className="rounded-lg border border-[#DCE4E0] bg-[#F7FAF9] p-4 space-y-2">
                      <p className="text-xs font-semibold text-[#073B2E] uppercase tracking-wide">Details</p>
                      {Object.entries(detailNotification.details).map(([key, value]) => (
                        value != null && value !== '' && (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-[#0E1B16] opacity-70">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium text-[#073B2E]">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={closeDetailAndMarkRead}
                      className="bg-[#073B2E] hover:bg-[#052E22] text-white rounded-full px-6"
                    >
                      OK
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {notifications.length === 0 && (
            <Card className="bg-white border-[#DCE4E0]">
              <CardContent className="p-12 text-center">
                <Bell className="h-16 w-16 mx-auto mb-4 text-[#0E1B16] opacity-20" />
                <h3 className="text-lg font-semibold text-[#073B2E] mb-2">No notifications</h3>
                <p className="text-sm text-[#0E1B16] opacity-60">
                  You're all caught up! We'll notify you when something new happens.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Notifications;
